import type { DocumentMoveInput } from "../../api/documents";

/**
 * The sidebar's drag-and-drop model (note 03 §4: reorder within a parent,
 * reparent under another document, move between collections).
 *
 * Positions come from explicit drop zones rendered between rows rather than
 * from pointer geometry. That is a deliberate choice: geometry is unreachable
 * in a test environment with no layout, and an explicit target is also the only
 * version of this interaction that a keyboard could ever drive.
 */

export interface SidebarDrag {
  documentId: string;
  title: string;
  collectionId: string;
  parentDocumentId: string | null;
  /** The item's own position among its siblings, before the drag. */
  index: number;
}

export interface DropBetween {
  type: "between";
  collectionId: string;
  parentDocumentId: string | null;
  /** Insertion position in the rendered sibling list. */
  position: number;
}

export interface DropInside {
  type: "inside";
  collectionId: string;
  documentId: string;
}

export type SidebarDropTarget = DropBetween | DropInside;

export function sameTarget(
  left: SidebarDropTarget | null,
  right: SidebarDropTarget | null,
): boolean {
  if (left === null || right === null) return left === right;
  if (left.type !== right.type) return false;
  if (left.type === "inside" && right.type === "inside") {
    return left.documentId === right.documentId;
  }
  if (left.type === "between" && right.type === "between") {
    return (
      left.collectionId === right.collectionId &&
      left.parentDocumentId === right.parentDocumentId &&
      left.position === right.position
    );
  }
  return false;
}

/**
 * The `documents.move` body a drop implies, or `null` when the drop would not
 * change anything. The server detaches the document before re-inserting it, so
 * a drop *after* its own old position within the same parent is one place
 * further left than the rendered zone suggests.
 */
export function moveInputFor(
  drag: SidebarDrag,
  target: SidebarDropTarget,
): DocumentMoveInput | null {
  if (target.type === "inside") {
    if (target.documentId === drag.documentId) return null;
    return {
      id: drag.documentId,
      collectionId: target.collectionId,
      parentDocumentId: target.documentId,
      index: 0,
    };
  }

  const sameParent =
    drag.collectionId === target.collectionId && drag.parentDocumentId === target.parentDocumentId;
  const index = sameParent && drag.index < target.position ? target.position - 1 : target.position;
  if (sameParent && index === drag.index) return null;

  return {
    id: drag.documentId,
    collectionId: target.collectionId,
    parentDocumentId: target.parentDocumentId,
    index,
  };
}
